import { useMemo, useRef, useState } from 'react';

import { Markdown } from 'components/Markdown';
import {
  ArtifactComponent,
  ArtifactLoading,
} from 'components/maxi-ai/artifacts/ArtifactComponent';
import { FeedbackThumbs } from 'components/maxi-ai/feedback/FeedbackThumbs';

import { useMaxiAI } from 'hooks/maxi-ai/useMaxiAI';
import { useNotification } from 'hooks/utils/useNotification';

import { Logger } from 'utils/logger';
import { parseContentSegments } from 'utils/maxi-ai/artifactParser';

import { useMaxiAIStore } from 'store/maxi-ai.store';

import type { ChatMessage, Artifact } from 'types/maxi-ai';

// Markdown styling with custom heading styles
const MARKDOWN_STYLES = [
  // Base text styles
  'text-base leading-relaxed',
  // List styles
  '[&_ol]:pl-4 [&_ul]:pl-4 [&_li]:ml-4 [&_li]:mb-1',
  // Text formatting
  '[&_strong]:font-medium [&_strong]:text-color-primary',
  '[&_em]:italic [&_em]:text-gray-700',
  // Heading styles - H1 (largest)
  '[&_h1]:text-2xl [&_h1]:font-medium [&_h1]:my-4 [&_h1]:border-b [&_h1]:border-gray-200 [&_h1]:pb-2 [&_h1]:text-color-primary',
  // H2
  '[&_h2]:text-xl [&_h2]:font-medium [&_h2]:my-3 [&_h2]:text-color-primary',
  // H3
  '[&_h3]:text-lg [&_h3]:font-medium [&_h3]:my-2 [&_h3]:text-color-primary',
  // H4
  '[&_h4]:text-base [&_h4]:font-medium [&_h4]:my-2 [&_h4]:text-color-primary',
  // Paragraphs
  '[&_p]:mb-3 [&_p]:leading-relaxed',
].join(' ');

interface MessageContentProps {
  message: ChatMessage;
  className?: string;
}

export function MessageContent({ message, className }: MessageContentProps) {
  const lastTextRef = useRef<string>('');
  const lastSegmentsRef = useRef<ReturnType<typeof parseContentSegments>>([]);

  const { updateMessage } = useMaxiAIStore();
  const { addFeedback, artifacts } = useMaxiAI();
  const notification = useNotification();
  const [pendingFeedbackIds, setPendingFeedbackIds] = useState<string[]>([]);

  // Optimize parsing to prevent artifact flickering
  // Track artifact states to detect changes
  const artifactStatesRef = useRef<
    Map<string, { status: string; hasContent: boolean }>
  >(new Map());

  const contentSegments = useMemo(() => {
    const currentText = message.text || '';
    const lastText = lastTextRef.current;

    // Check if artifacts have changed status
    let artifactsChanged = false;
    const currentArtifactStates = new Map<
      string,
      { status: string; hasContent: boolean }
    >();
    Object.entries(artifacts).forEach(([id, artifact]) => {
      const state = { status: artifact.status, hasContent: !!artifact.content };
      currentArtifactStates.set(id, state);

      const lastState = artifactStatesRef.current.get(id);
      if (
        !lastState ||
        lastState.status !== state.status ||
        lastState.hasContent !== state.hasContent
      ) {
        artifactsChanged = true;
      }
    });

    // If text hasn't changed and artifacts haven't changed, return cached segments
    if (currentText === lastText && !artifactsChanged) {
      return lastSegmentsRef.current;
    }

    // Update artifact states ref
    artifactStatesRef.current = currentArtifactStates;

    // If new text is just appended (most common case during streaming) AND artifacts haven't changed
    if (
      !artifactsChanged &&
      currentText.startsWith(lastText) &&
      currentText.length > lastText.length
    ) {
      const newPortion = currentText.slice(lastText.length);

      // If new portion doesn't contain artifact tags (opening or closing), just append as text
      if (
        !newPortion.includes('<table') &&
        !newPortion.includes('</table>') &&
        !newPortion.includes('<line_chart') &&
        !newPortion.includes('</line_chart>') &&
        !newPortion.includes('<bar_chart') &&
        !newPortion.includes('</bar_chart>')
      ) {
        const segments = [...lastSegmentsRef.current];
        const lastSegment = segments[segments.length - 1];

        if (lastSegment?.type === 'text') {
          // Append to existing text segment, preserving the same ID
          segments[segments.length - 1] = {
            ...lastSegment,
            content: (lastSegment.content as string) + newPortion,
          };
        } else {
          // Add new text segment with UUID
          segments.push({
            id: crypto.randomUUID(),
            type: 'text',
            content: newPortion,
          });
        }

        lastTextRef.current = currentText;
        lastSegmentsRef.current = segments;
        return segments;
      }
    }

    // Full re-parse needed (contains artifacts or content changed significantly)
    const newSegments = parseContentSegments(currentText, artifacts);
    lastTextRef.current = currentText;
    lastSegmentsRef.current = newSegments;
    return newSegments;
  }, [message, artifacts]);

  // Simplified feedback submission
  const handleThumbSubmit = async ({
    messageId,
    traceId,
    thumbsUp,
    text,
  }: {
    messageId: string;
    traceId: string;
    thumbsUp: boolean;
    text: string;
  }) => {
    setPendingFeedbackIds((prev) => [...prev, messageId]);

    try {
      await addFeedback(traceId, thumbsUp, text || undefined);
      updateMessage(messageId, (m) => ({
        ...m,
        feedback: {
          thumbs_up: thumbsUp,
          feedback: text || undefined,
        },
      }));
      notification.success({
        title: 'Feedback Submitted',
        description: 'Thank you for your feedback!',
      });
    } catch (err) {
      Logger.error('Failed to submit feedback:', err);
    } finally {
      setPendingFeedbackIds((prev) => prev.filter((id) => id !== messageId));
    }
  };

  return (
    <div className={className}>
      {contentSegments.map((segment) => {
        // Use the stable id from the segment instead of generating keys
        const segmentKey = segment.id;

        switch (segment.type) {
          case 'text':
            return (
              <Markdown
                key={segmentKey}
                className={MARKDOWN_STYLES}
                text={segment.content as string}
              />
            );
          case 'artifact':
            return (
              <ArtifactComponent
                key={segmentKey}
                artifact={segment.content as Artifact}
              />
            );
          case 'artifact-loading':
            return (
              <ArtifactLoading
                key={segmentKey}
                artifactType={segment.artifactType!}
              />
            );
          default:
            return null;
        }
      })}

      {/* Feedback section */}
      {message.trace_id && (
        <div className="flex w-full justify-end mt-2">
          <FeedbackThumbs
            messageId={message.id}
            traceId={message.trace_id}
            isSubmitting={pendingFeedbackIds.includes(message.id)}
            onSubmit={handleThumbSubmit}
            selected={
              message.feedback?.thumbs_up === true
                ? 'like'
                : message.feedback?.thumbs_up === false
                  ? 'dislike'
                  : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
